import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { MapPin, Calendar, ArrowLeft, ChevronRight, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import LocationPicker from '../components/LocationPicker';
import { DateTimePicker } from '../components/ui/DateTimePicker';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const resolveName = (nameObj, lang) =>
  nameObj?.[lang] ?? nameObj?.['es'] ?? nameObj?.['en'] ?? '';

const PricingBadge = ({ model, t }) => {
  const key = model === 'fixed' ? 'pricing_fixed' : model === 'quote' ? 'pricing_quote' : 'pricing_both';
  const variant = model === 'fixed' ? 'default' : model === 'quote' ? 'secondary' : 'outline';
  return <Badge variant={variant} className="text-xs">{t(`service.${key}`)}</Badge>;
};

// ── Step 1: Category grid ────────────────────────────────────────────────────
const CategoryStep = ({ selected, onSelect }) => {
  const { t, i18n } = useTranslation();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const fetch = async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await axios.get(`${API}/categories`);
      setCategories(res.data);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetch(); }, []);

  if (loading) return (
    <div className="grid sm:grid-cols-3 gap-3">
      {[...Array(6)].map((_, i) => (
        <div key={i} className="h-16 rounded-lg bg-gray-100 animate-pulse" />
      ))}
    </div>
  );

  if (error) return (
    <div className="text-center py-6">
      <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-yellow-500" />
      <p className="text-sm text-muted-foreground mb-3">Could not load categories. Tap to retry.</p>
      <Button variant="outline" size="sm" onClick={fetch}>Retry</Button>
    </div>
  );

  return (
    <div>
      <p className="text-sm font-medium mb-3">{t('service.select_category')}</p>
      <div className="grid sm:grid-cols-3 gap-3">
        {categories.map(cat => (
          <button
            key={cat.id}
            type="button"
            onClick={() => onSelect(cat)}
            className={`text-left px-4 py-3 rounded-lg border-2 transition-all text-sm font-medium
              ${selected?.id === cat.id
                ? 'border-primary bg-primary/5 text-primary'
                : 'border-gray-200 hover:border-gray-400 text-foreground'}`}
          >
            {resolveName(cat.name, i18n.language)}
          </button>
        ))}
      </div>
    </div>
  );
};

// ── Step 2: Subcategory list ─────────────────────────────────────────────────
const SubcategoryStep = ({ category, selected, onSelect, onBack }) => {
  const { t, i18n } = useTranslation();
  const [subcategories, setSubcategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await axios.get(`${API}/categories/${category.id}/subcategories`);
      setSubcategories(res.data);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [category.id]);

  useEffect(() => { fetch(); }, [fetch]);

  if (loading) return (
    <div className="space-y-2">
      {[...Array(4)].map((_, i) => (
        <div key={i} className="h-16 rounded-lg bg-gray-100 animate-pulse" />
      ))}
    </div>
  );

  if (error) return (
    <div className="text-center py-6">
      <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-yellow-500" />
      <p className="text-sm text-muted-foreground mb-3">Could not load service types.</p>
      <Button variant="outline" size="sm" onClick={fetch}>Retry</Button>
    </div>
  );

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <button type="button" onClick={onBack} className="text-sm text-primary hover:underline flex items-center gap-1">
          <ArrowLeft className="w-3.5 h-3.5" />
          {resolveName(category.name, i18n.language)}
        </button>
        <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
        <span className="text-sm font-medium">{t('service.select_subcategory')}</span>
      </div>
      <div className="space-y-2">
        {subcategories.map(sub => (
          <button
            key={sub.id}
            type="button"
            onClick={() => onSelect(sub)}
            className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-all
              ${selected?.id === sub.id
                ? 'border-primary bg-primary/5'
                : 'border-gray-200 hover:border-gray-400'}`}
          >
            <div className="flex items-center justify-between">
              <span className="font-medium text-sm">{resolveName(sub.name, i18n.language)}</span>
              <div className="flex gap-1.5">
                <PricingBadge model={sub.pricing_model} t={t} />
                {sub.is_urgent && (
                  <Badge className="text-xs bg-red-100 text-red-700 border-red-200">
                    {t('service.urgent_available')}
                  </Badge>
                )}
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

// ── Main page ────────────────────────────────────────────────────────────────
const ServiceRequestPage = () => {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  // Selection state (separate from form payload so resetting category doesn't clear form fields)
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);

  // Step: 'category' | 'subcategory' | 'details'
  const [step, setStep] = useState('category');

  const [formData, setFormData] = useState({
    category_id: '',
    subcategory_id: '',
    title: { es: '', en: '', fr: '' },
    description: { es: '', en: '', fr: '' },
    preferred_date: '',
  });
  const [location, setLocation] = useState(null); // { address, lat, lng } from LocationPicker
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({ location: false, date: false });

  useEffect(() => {
    if (user?.role !== 'client') navigate('/dashboard');
  }, [user, navigate]);

  const handleCategorySelect = (cat) => {
    setSelectedCategory(cat);
    setSelectedSubcategory(null);
    setFormData(prev => ({ ...prev, category_id: cat.id, subcategory_id: '' }));
    setStep('subcategory');
  };

  const handleSubcategorySelect = (sub) => {
    setSelectedSubcategory(sub);
    setFormData(prev => ({ ...prev, subcategory_id: sub.id }));
    setStep('details');
  };

  const handleBackToCategory = () => {
    setSelectedCategory(null);
    setSelectedSubcategory(null);
    setFormData(prev => ({ ...prev, category_id: '', subcategory_id: '' }));
    setStep('category');
  };

  const handleBackToSubcategory = () => {
    setSelectedSubcategory(null);
    setFormData(prev => ({ ...prev, subcategory_id: '' }));
    setStep('subcategory');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.subcategory_id) {
      toast.error(t('service.select_subcategory'));
      return;
    }
    const errors = {
      location: !location?.address,
      date: !formData.preferred_date,
    };
    setFieldErrors(errors);
    if (errors.location || errors.date) return;
    setLoading(true);
    try {
      await axios.post(`${API}/service-requests`, {
        category_id: formData.category_id,
        subcategory_id: formData.subcategory_id,
        title: formData.title,
        description: formData.description,
        location: location || undefined,
        preferred_date: formData.preferred_date,
      });
      toast.success('Service request created successfully!');
      navigate('/dashboard');
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail?.error === 'client_email_unverified' || detail?.error === 'client_id_required') {
        const msg = detail.message?.[i18n.language] || detail.message?.es;
        toast.error(msg || t('verify.required_to_book'));
        navigate('/verify');
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Failed to create request');
      }
    } finally {
      setLoading(false);
    }
  };

  const updateTranslatedField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: { es: value, en: value, fr: value } }));
  };

  const resolveName = (nameObj) => nameObj?.[i18n.language] ?? nameObj?.['es'] ?? nameObj?.['en'] ?? '';

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
        <Card data-testid="service-request-form">
          <CardHeader>
            <CardTitle className="text-3xl" style={{ fontFamily: 'Outfit' }}>
              {t('service.request_title')}
            </CardTitle>
            {/* Breadcrumb when past category step */}
            {step !== 'category' && (
              <div className="flex items-center gap-1 text-sm text-muted-foreground mt-1">
                <button type="button" onClick={handleBackToCategory} className="hover:text-primary hover:underline">
                  {resolveName(selectedCategory?.name)}
                </button>
                {step === 'details' && selectedSubcategory && (
                  <>
                    <ChevronRight className="w-3.5 h-3.5" />
                    <button type="button" onClick={handleBackToSubcategory} className="hover:text-primary hover:underline">
                      {resolveName(selectedSubcategory?.name)}
                    </button>
                  </>
                )}
              </div>
            )}
          </CardHeader>

          <CardContent>
            {/* ── Step: category ── */}
            {step === 'category' && (
              <CategoryStep selected={selectedCategory} onSelect={handleCategorySelect} />
            )}

            {/* ── Step: subcategory ── */}
            {step === 'subcategory' && selectedCategory && (
              <SubcategoryStep
                category={selectedCategory}
                selected={selectedSubcategory}
                onSelect={handleSubcategorySelect}
                onBack={handleBackToCategory}
              />
            )}

            {/* ── Step: details form ── */}
            {step === 'details' && (
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Subcategory validation sentinel */}
                {!formData.subcategory_id && (
                  <p className="text-sm text-red-600">{t('service.select_subcategory')}</p>
                )}

                <div className="space-y-2">
                  <Label htmlFor="title">{t('service.title')}</Label>
                  <Input
                    id="title"
                    data-testid="title-input"
                    placeholder="e.g., Fix leaking kitchen pipe"
                    value={formData.title[i18n.language]}
                    onChange={(e) => updateTranslatedField('title', e.target.value)}
                    required
                    className="h-12"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">{t('service.description')}</Label>
                  <Textarea
                    id="description"
                    data-testid="description-input"
                    placeholder="Describe what you need in detail..."
                    value={formData.description[i18n.language]}
                    onChange={(e) => updateTranslatedField('description', e.target.value)}
                    required
                    rows={5}
                  />
                </div>

                <div className="space-y-2">
                  <Label>
                    <MapPin className="w-4 h-4 inline mr-2" />
                    {t('service.address')}
                    <span className="text-red-500 ml-1">*</span>
                  </Label>
                  <LocationPicker
                    onLocationSelect={(loc) => {
                      setLocation(loc);
                      if (loc?.address) setFieldErrors(prev => ({ ...prev, location: false }));
                    }}
                    placeholder={t('location.search_placeholder')}
                  />
                  {fieldErrors.location && (
                    <p className="text-xs text-red-600">{t('service.location_required')}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>
                    <Calendar className="w-4 h-4 inline mr-2" />
                    {t('service.preferred_date')}
                    <span className="text-red-500 ml-1">*</span>
                  </Label>
                  <DateTimePicker
                    value={formData.preferred_date}
                    onChange={(iso) => {
                      setFormData(prev => ({ ...prev, preferred_date: iso }));
                      if (iso) setFieldErrors(prev => ({ ...prev, date: false }));
                    }}
                    placeholder={t('datetime.no_date')}
                    hasError={fieldErrors.date}
                  />
                  {fieldErrors.date && (
                    <p className="text-xs text-red-600">{t('service.date_required')}</p>
                  )}
                </div>

                <div className="flex gap-4">
                  <Button type="button" variant="outline" onClick={() => navigate('/dashboard')} className="flex-1">
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={loading || !formData.subcategory_id}
                    className="flex-1 h-12"
                    style={{ background: 'hsl(48 100% 50%)', color: 'hsl(215 25% 12%)' }}
                    data-testid="submit-request-button"
                  >
                    {loading ? 'Creating...' : t('service.submit')}
                  </Button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
  );
};

export default ServiceRequestPage;
